import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Divider, Grid, Group, LoadingOverlay, Text } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { MainLayout } from '../../layouts'
import { useAppDispatch, useAppSelector } from '../../../store'
import { GroupType } from '../../../store/types'
import { BackListButton, ContentBox, ItemActionsMenu } from '../../partials'
import { fetchShowGroup, resetShowGroup } from '../../../store/features/groups/showGroupSlice'
import { fetchDeleteGroup, resetDeleteGroup } from '../../../store/features/groups/deleteGroupSlice'

const Show = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [data, setData] = useState<GroupType | null>(null)
  const { isLoading, response, error } = useAppSelector(state => state.showGroup)
  const {
    isLoading: deleteIsLoading,
    response: deleteResponse,
    error: deleteError
  } = useAppSelector(state => state.deleteGroup)

  useEffect(() => {
    if (response === null && data === null) {
      ;(async () => {
        await dispatch(fetchShowGroup(id as string))
      })()
    }
  }, [response, data, id, dispatch])

  useEffect(() => {
    if (response !== null) {
      setData(response.data)

      if (data !== null) {
        dispatch(resetShowGroup())
      }
    }
  }, [response, dispatch, data])

  useEffect(() => {
    if (error !== null) {
      if (error.response.status === 401 && id) {
        ;(async () => {
          dispatch(resetShowGroup())
          await dispatch(fetchShowGroup(id))
        })()
      } else if (error.response.status === 404) {
        dispatch(resetShowGroup())
        showNotification({
          color: 'red',
          title: t('error'),
          message: t(error.data.message)
        })

        navigate('/groups')
      } else {
        dispatch(resetShowGroup())
        showNotification({
          color: 'red',
          title: t('error'),
          message: t('errors.unknown')
        })

        navigate('/groups')
      }
    }
  }, [error, id, dispatch, t, navigate])

  useEffect(() => {
    if (deleteResponse !== null) {
      dispatch(resetShowGroup())
      dispatch(resetDeleteGroup())

      showNotification({
        color: 'green',
        title: t('successful'),
        message: t('deleted')
      })

      navigate('/groups')
    }
  }, [deleteResponse, t, dispatch, navigate])

  useEffect(() => {
    if (deleteError !== null) {
      dispatch(resetShowGroup())

      showNotification({
        color: 'red',
        title: t('unsuccessful'),
        message: deleteError.response.data ? deleteError.response.data.message : t('errors.unknown')
      })
    }
  }, [deleteError, t, dispatch])

  const handleDelete = async (id: string) => {
    await dispatch(fetchDeleteGroup(id))
  }

  return (
    <MainLayout>
      <LoadingOverlay visible={isLoading || deleteIsLoading} />
      {data && (
        <>
          <ContentBox title={t('content_actions.show')}>
            <h4>{t('id')}:</h4>
            <Text>{data.id}</Text>

            <Divider
              variant="dashed"
              size="xs"
              mt="sm"
            />

            <h4>{t('name')}:</h4>
            <Text>{data.name}</Text>
          </ContentBox>
          <ContentBox>
            <Grid>
              <Grid.Col span={6}>
                <BackListButton to="/groups" />
              </Grid.Col>
              <Grid.Col span={6}>
                <Group
                  position="right"
                  pr="md"
                >
                  <ItemActionsMenu
                    edit={`/groups/${data.id}/edit`}
                    onDelete={() => handleDelete(data.id)}
                  />
                </Group>
              </Grid.Col>
            </Grid>
          </ContentBox>
        </>
      )}
    </MainLayout>
  )
}

export default Show
